import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, IconButton } from "@mui/material";

import { RobotoLinkHoverUnderline } from "@/shared/components/RobotoLinkHoverUnderline";
import { FormattedDateTime } from "@/shared/datetime";
import type { MetricRecord } from "@/shared/domain/metrics";
import { useMetricDefinition } from "@/shared/domain/metrics/hooks";
import {
  latestRecord,
  type MetricTreeNode,
} from "@/shared/domain/metrics/metricTree";
import { Goto } from "@/shared/navigation/Goto";
import { Principal } from "@/shared/principal";

import styles from "./MetricDetailCard.module.css";

const EM_DASH = "—";

export interface MetricDetailCardProps {
  /** The selected node to describe. Always carries at least one record. */
  node: MetricTreeNode<MetricRecord[]>;
  /** Current org id — scopes the definition lookup that yields the description. */
  orgId: string;
  /** Dismiss the card and clear the page's selection. */
  onClose: () => void;
}

/**
 * Detail card for a single selected metric. Pops out to the right of the
 * session-metrics table (the page keeps it sticky to the scroll position) and
 * surfaces, top to bottom: the metric's name, its newest value, when that
 * value was computed, who/what produced it, and the metric's description.
 *
 * A leaf folds every record sharing its name; the value, timestamp, and
 * attribution all come from the most recently published record. The
 * description lives on the metric *definition* (not the record), so it is
 * fetched lazily by name while the card is mounted.
 */
export function MetricDetailCard({
  node,
  orgId,
  onClose,
}: MetricDetailCardProps) {
  const latest = latestRecord(node.data);
  const definition = useMetricDefinition(node.id, orgId);

  // invocation_id is the preferred attribution; fall back to the publisher when
  // the metric was not produced by an invocation. published_by is a serialized
  // principal ("user:user_123"); strip it to the bare id the same way the data
  // tables do (see SearchResultViews/tableLogic), leaving non-principal values
  // (e.g. legacy emails) untouched.
  const attributionLabel = latest?.invocation_id
    ? "Invocation"
    : "Published by";
  const publisher =
    latest?.published_by === undefined
      ? undefined
      : Principal.fromString(latest.published_by).ok?.unqualifiedId ??
        latest.published_by;
  const attribution = latest?.invocation_id ?? publisher;

  return (
    <aside className={styles.card} aria-label={`Metric detail for ${node.id}`}>
      <div className={styles.header}>
        <div className={styles.heading}>
          <code className={styles.fqName}>{node.id}</code>
          <span className={styles.leafName}>{node.label}</span>
        </div>
        <IconButton size="small" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={styles.valueSection}>
        <span className={styles.rowLabel}>Value</span>
        <span className={styles.value}>
          {latest
            ? `${latest.value}${latest.unit ? ` ${latest.unit}` : ""}`
            : EM_DASH}
        </span>
      </div>

      <dl className={styles.rows}>
        <div className={styles.row}>
          <dt className={styles.rowLabel}>Last Published</dt>
          <dd className={styles.rowValue}>
            {latest ? <FormattedDateTime value={latest.published} /> : EM_DASH}
          </dd>
        </div>

        <div className={styles.row}>
          <dt className={styles.rowLabel}>{attributionLabel}</dt>
          <dd className={styles.rowValue}>{attribution ?? EM_DASH}</dd>
        </div>

        <div className={styles.row}>
          <dt className={styles.rowLabel}>Device</dt>
          <dd className={styles.rowValue}>
            {latest?.device_id ? (
              <RobotoLinkHoverUnderline
                to={Goto.createDeviceMetricsRoute(latest.device_id)}
              >
                {latest.device_id}
              </RobotoLinkHoverUnderline>
            ) : (
              EM_DASH
            )}
          </dd>
        </div>

        <div className={styles.row}>
          <dt className={styles.rowLabel}>Description</dt>
          <dd className={styles.rowValue}>
            {definition.isLoading ? (
              <CircularProgress size={14} aria-label="Loading description" />
            ) : (
              definition.data?.description ?? EM_DASH
            )}
          </dd>
        </div>
      </dl>
    </aside>
  );
}
